import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { ArrowLeft, Play, UserPlus, Filter, Crown, Pencil, ListOrdered, History, Trash2 } from 'lucide-react'
import { RootState } from '../store'
import { setSingleRoom } from '../store/roomsSlice'
import roomsService from '../services/roomsService'
import formula1Service from '../services/formula1Service'
import { Formula1PlayerPoints, Formula1Race, Formula1Prediction } from '../types/formula1'
import { pickUpcomingFormula1Races } from '../utils/formula1RaceTiming'
import toast from 'react-hot-toast'
import Formula1PlayerItem from '../components/Formula1PlayerItem'
import InvitePlayerModal from '../components/InvitePlayerModal'
import BestOfModalFormula1 from '../components/BestOfModalFormula1'
import Formula1GamesAndPredictionsModal from '../components/Formula1GamesAndPredictionsModal'
import Formula1RacePredictionsModal from '../components/Formula1RacePredictionsModal'
import EditFormula1RoomModal from '../components/EditFormula1RoomModal'
import DeleteRoomModal from '../components/DeleteRoomModal'
import { useTranslation } from '../hooks/use-i18n'
import { canManageRoom } from '../utils/roomPermissions'
import { useRoomMessenger } from '../hooks/useRoomMessenger'
import RoomChatRulesActions from '../components/room/RoomChatRulesActions'
import RoomMessengerModal from '../components/room/RoomMessengerModal'
import { useQuery } from '@tanstack/react-query'
import Formula1RulesModal from '../components/Formula1RulesModal'
import PlayWorldFromRoomModal from '../components/PlayWorldFromRoomModal'
import PlayWorldRoomBanner from '../components/PlayWorldRoomBanner'
import LeaveWorldFootballModal from '../components/LeaveWorldFootballModal'

const Formula1Room: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { singleRoom } = useSelector((state: RootState) => state.rooms)
  const { user } = useSelector((state: RootState) => state.auth)
  const isOwner = canManageRoom(singleRoom, user?._id)
  const {
    showMessengerModal,
    setShowMessengerModal,
    unreadMessageCount,
    setUnreadMessageCount,
  } = useRoomMessenger(roomId)

  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [leaderboard, setLeaderboard] = useState<Formula1PlayerPoints[]>([])
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(true)
  const [races, setRaces] = useState<Formula1Race[]>([])
  const [, setIsLoadingRaces] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteRoomModal, setShowDeleteRoomModal] = useState(false)
  const [showBestOfModal, setShowBestOfModal] = useState(false)
  const [isLoadingBestOf, setIsLoadingBestOf] = useState(false)
  const [bestOfPredictions, setBestOfPredictions] = useState<Formula1Prediction[]>([])
  const [showGamesAndPredictionsModal, setShowGamesAndPredictionsModal] = useState(false)
  const [racesModalMode, setRacesModalMode] = useState<'upcoming' | 'played'>('upcoming')
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [showPlayWorldModal, setShowPlayWorldModal] = useState(false)
  const [showLeaveWorldModal, setShowLeaveWorldModal] = useState(false)
  const [selectedRaceForPredictions, setSelectedRaceForPredictions] = useState<Formula1Race | null>(null)
  const [showRacePredictionsModal, setShowRacePredictionsModal] = useState(false)

  const { data: publicFormula1Status, refetch: refetchPublicF1Status } = useQuery({
    queryKey: ['public-formula1-status', roomId],
    queryFn: () => roomsService.getPublicFormula1Status(roomId!),
    enabled: Boolean(roomId),
    staleTime: 30 * 1000,
  })
  const showPlayWorldCta = Boolean(
    publicFormula1Status?.syncEnabled && !publicFormula1Status.fullyEnrolled,
  )
  const showViewWorldLink = Boolean(
    publicFormula1Status?.syncEnabled && publicFormula1Status.fullyEnrolled,
  )

  // First upcoming race: same logic as Play button - only this race is clickable in the list
  const firstUpcomingRaceId = useMemo(() => {
    const list = Array.isArray(races) ? races : []
    const withDrivers = list.filter((r) => {
      const d = Array.isArray(r.drivers) ? r.drivers : []
      return d.length > 0
    })
    const next = pickUpcomingFormula1Races(withDrivers)[0]
    return next?.id ?? next?._id ?? null
  }, [races])

  // Sort players by total points
  const sortedPlayers = useMemo(() => {
    if (!Array.isArray(leaderboard) || leaderboard.length === 0) {
      return []
    }
    
    return [...leaderboard].sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) {
        return b.totalPoints - a.totalPoints
      }
      const nameA = a.player?.name || ''
      const nameB = b.player?.name || ''
      return nameA.localeCompare(nameB)
    })
  }, [leaderboard])

  const fetchRoomData = async (forceRefresh = false) => {
    if (!roomId) return
    if (forceRefresh) setIsRefreshing(true)
    else setIsLoadingPage(true)
    try {
      const response = await roomsService.getRoom(roomId)
      dispatch(setSingleRoom(response))
      await Promise.all([loadLeaderboard(), loadRaces()])
    } catch (error: any) {
      console.error('Failed to fetch room data:', error)
      toast.error(forceRefresh ? 'Failed to refresh' : 'Failed to load room')
    } finally {
      setIsLoadingPage(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    if (roomId) fetchRoomData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  const loadLeaderboard = async () => {
    if (!roomId) return

    try {
      setIsLoadingLeaderboard(true)
      const data = await formula1Service.getLeaderboard(roomId)
      setLeaderboard(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      toast.error('Failed to load leaderboard')
      setLeaderboard([])
    } finally {
      setIsLoadingLeaderboard(false)
    }
  }

  const loadRaces = async () => {
    if (!roomId) return

    try {
      setIsLoadingRaces(true)
      const data = await formula1Service.getRaces({ limit: 50 })
      setRaces(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading races:', error)
      toast.error('Failed to load races')
      setRaces([])
    } finally {
      setIsLoadingRaces(false)
    }
  }

  const handleOpenBestOf = async () => {
    if (!roomId) return
    setShowBestOfModal(true)
    setIsLoadingBestOf(true)
    setBestOfPredictions([])
    try {
      const list = await formula1Service.getRoomPredictions(roomId)
      setBestOfPredictions(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Failed to load best predictions:', err)
      toast.error('Failed to load best predictions')
      setBestOfPredictions([])
    } finally {
      setIsLoadingBestOf(false)
    }
  }

  const handlePlaying = () => {
    if (!roomId || !singleRoom?._id) {
      toast.error('Room data not loaded')
      return
    }
    if (firstUpcomingRaceId) {
      navigate(`/formula1-race/${roomId}/${firstUpcomingRaceId}`)
      return
    }
    navigate(`/formula1/${roomId}/playing`)
  }

  if (isLoadingPage || !singleRoom) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (singleRoom.gameType !== 'formula1') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-display-2 font-bold text-foreground mb-4">Invalid Room Type</h2>
          <p className="text-muted-foreground mb-6">This is not a Formula 1 room.</p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const playerCount = sortedPlayers.length
  return (
    <div className="min-h-screen bg-background">
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          {/* Room Header - same structure as football room: title + refresh, edit, add player */}
          <div className="mb-5 sm:mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-3 sm:mb-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 text-small sm:text-nav text-muted-foreground hover:text-foreground transition-colors group"
              >
                <ArrowLeft size={14} className="sm:w-4 sm:h-4 group-hover:-translate-x-0.5 transition-transform" />
                {t('dashboard.backToDashboard')}
              </Link>
              {isOwner && roomId && (
                <button
                  type="button"
                  onClick={() => setShowDeleteRoomModal(true)}
                  className="inline-flex items-center gap-1 text-xs text-destructive/90 hover:text-destructive"
                >
                  <Trash2 size={12} />
                  {t('room.delete.action')}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <h1 className="font-display text-xl sm:text-display-1 font-bold tracking-tight text-foreground truncate flex items-center gap-2">
                ??? {singleRoom.name}
                {isRefreshing && (
                  <span className="inline-block w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" title={t('roomPage.updating')} />
                )}
              </h1>
              <button
                type="button"
                onClick={() => fetchRoomData(true)}
                disabled={isRefreshing}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.refresh')}
              >
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                  title={t('room.editRoom')}
                >
                  <Pencil size={18} className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowInviteModal(true)}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.invitePlayer')}
              >
                <UserPlus size={18} className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            <p className="text-small sm:text-body-lg text-muted-foreground mt-1">
              {playerCount} player{playerCount !== 1 ? 's' : ''} - Formula 1
            </p>
            {(showPlayWorldCta || showViewWorldLink) && (
              <PlayWorldRoomBanner
                className="mt-3"
                showPlayWorldCta={showPlayWorldCta}
                showViewWorldLink={showViewWorldLink}
                worldPath="/public/formula1"
                onOpenModal={() => setShowPlayWorldModal(true)}
                onLeaveWorld={() => setShowLeaveWorldModal(true)}
              />
            )}
          </div>

          {/* Action Buttons - Play, Best of, Played, Upcoming */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-10">
            <button
              onClick={handlePlaying}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-primary text-primary-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <Play size={14} className="sm:w-4 sm:h-4" /> {t('roomPage.play')}
            </button>
            <button
              onClick={handleOpenBestOf}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-warning to-destructive text-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <Crown size={14} className="sm:w-4 sm:h-4" /> {t('roomPage.bestOf')}
            </button>
            <button
              onClick={() => {
                setRacesModalMode('played')
                setShowGamesAndPredictionsModal(true)
              }}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-sky-800 to-sky-400 text-white font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <History size={14} className="sm:w-4 sm:h-4" /> {t('roomPage.played')}
            </button>
            <button
              onClick={() => {
                setRacesModalMode('upcoming')
                setShowGamesAndPredictionsModal(true)
              }}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-success/80 to-primary/80 text-foreground font-semibold text-small sm:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1.5 sm:gap-2"
            >
              <ListOrdered size={14} className="sm:w-4 sm:h-4" /> {t('formula1.upcomingRaces')}
            </button>
          </div>

          {/* Leaderboard Section - same card and spacing as football rankings */}
          <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 mb-6 sm:mb-8">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 mb-3 sm:mb-5">
              <h2 className="font-display text-lg sm:text-display-2 font-bold flex items-center gap-1.5 sm:gap-2 text-foreground col-start-1">
                ?? {t('roomPage.rankings')}
              </h2>
              <RoomChatRulesActions
                className="col-start-2"
                unreadCount={unreadMessageCount}
                onChatClick={() => setShowMessengerModal(true)}
                onRulesClick={() => setShowRulesModal(true)}
                showRules
              />
              <div className="flex justify-end col-start-3">
              <button className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all" aria-label="Filter">
                <Filter size={14} className="sm:w-4 sm:h-4" />
              </button>
              </div>
            </div>

            {isLoadingLeaderboard ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
              </div>
            ) : sortedPlayers.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-6xl mb-4">???</div>
                <h3 className="text-display-2 font-semibold text-foreground mb-2">{t('roomPage.noPlayers')}</h3>
                <p className="text-body-lg text-muted-foreground">{t('roomPage.noPlayersDesc')}</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-2.5">
                {sortedPlayers.map((player, index) => (
                  <Formula1PlayerItem
                    key={player.id || player._id || player.playerId}
                    player={player}
                    index={index}
                    roomId={roomId!}
                  />
                ))}
              </div>
            )}
          </div>

        </div>
      </main>

      {/* Best Of modal - predictions that scored points, by race */}
      {showBestOfModal && roomId && (
        <BestOfModalFormula1
          isOpen={showBestOfModal}
          roomId={roomId}
          onClose={() => setShowBestOfModal(false)}
          isLoading={isLoadingBestOf}
          predictions={bestOfPredictions}
        />
      )}

      {/* Games & Predictions modals (races list -> race detail with results + place predictions) */}
      {roomId && (
        <>
          <Formula1GamesAndPredictionsModal
            roomId={roomId}
            isOpen={showGamesAndPredictionsModal}
            onClose={() => setShowGamesAndPredictionsModal(false)}
            mode={racesModalMode}
            onSelectRace={(race) => {
              setSelectedRaceForPredictions(race)
              setShowRacePredictionsModal(true)
            }}
          />
          <Formula1RacePredictionsModal
            roomId={roomId}
            race={selectedRaceForPredictions}
            isOpen={showRacePredictionsModal}
            onClose={() => {
              setShowRacePredictionsModal(false)
              setSelectedRaceForPredictions(null)
            }}
          />
        </>
      )}

      {roomId && (
        <RoomMessengerModal
          roomId={roomId}
          isOpen={showMessengerModal}
          onClose={() => setShowMessengerModal(false)}
          onUnreadChange={setUnreadMessageCount}
        />
      )}

      {/* Invite by email modal - same as football room */}
      {showInviteModal && roomId && (
        <InvitePlayerModal
          roomId={roomId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            fetchRoomData(true)
          }}
        />
      )}

      {/* Edit room modal - name only for Formula 1 */}
      {showEditModal && singleRoom && roomId && (
        <EditFormula1RoomModal
          roomName={singleRoom.name}
          roomId={roomId}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => {
            dispatch(setSingleRoom(updated))
            setShowEditModal(false)
            toast.success('Room updated')
          }}
        />
      )}

      {showDeleteRoomModal && roomId && (
        <DeleteRoomModal
          roomId={roomId}
          roomName={singleRoom?.name}
          onClose={() => setShowDeleteRoomModal(false)}
        />
      )}

      {roomId && (
        <Formula1RulesModal
          isOpen={showRulesModal}
          roomId={roomId}
          onClose={() => setShowRulesModal(false)}
        />
      )}
      {showPlayWorldModal && publicFormula1Status && roomId && (
        <PlayWorldFromRoomModal
          roomId={roomId}
          variant="formula1"
          status={publicFormula1Status}
          onClose={() => setShowPlayWorldModal(false)}
          onSuccess={() => refetchPublicF1Status()}
        />
      )}
      {showLeaveWorldModal && (
        <LeaveWorldFootballModal
          variant="formula1"
          roomId={roomId}
          onClose={() => setShowLeaveWorldModal(false)}
          onSuccess={() => refetchPublicF1Status()}
        />
      )}
    </div>
  )
}

export default Formula1Room

